import * as Sentry from '@sentry/browser';
import { Form as AForm, Input } from 'antd';
import messages from 'containers/AccountInfoPage/messages';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import translations from 'translations';
import utilsMessages from 'utils/messages';
import Button from '../Button/Button';

const Form = styled(AForm)`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  .ant-form-item {
    flex: 1 1 0%;
    margin-bottom: 0px;
    .ant-form-item-label {
      white-space: nowrap;
    }
  }
  button[type='submit'] {
    margin-top: 22px;
  }
  @media (max-width: 991px) {
    flex-direction: column;
    button[type='submit'] {
      margin-top: 0px;
    }
  }
`;

const Password = styled(Input.Password)<any>``;

const ChangePasswordForm: FC<{ onSubmit: (data: any) => void }> = ({ onSubmit }) => {
  const [form] = Form.useForm();

  const handleSubmit = (values) => {
    onSubmit({
      password: values.password,
      currentPassword: values.currentPassword,
      cb: (success) => {
        if (success) {
          form.resetFields();
        }
      },
    });
  };

  const validateConfirmPassword = (_: any, value: string) => {
    const password = form.getFieldValue('password');

    if (!value) {
      return Promise.resolve();
    }

    if (password && value !== password) {
      return Promise.reject(translations(messages.confirmPasswordNotCorrect));
    }
    return Promise.resolve();
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => handleSubmit(values)}
      requiredMark={false}
      onFinishFailed={(error) => {
        Sentry.captureException(error, { level: Sentry.Severity.Warning });
      }}
    >
      <FormattedMessage {...messages.currentPassword}>
        {(currentPassword: string) => (
          <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: currentPassword }}>
            {(fieldRequired) => (
              <Form.Item
                name="currentPassword"
                label={currentPassword}
                colon={false}
                rules={[
                  {
                    required: true,
                    message: fieldRequired,
                  },
                ]}
                getValueFromEvent={(e) => e.target.value.trim()}
              >
                <Password size="large" />
              </Form.Item>
            )}
          </FormattedMessage>
        )}
      </FormattedMessage>
      <FormattedMessage {...messages.newPassword}>
        {(newPassword: string) => (
          <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: newPassword }}>
            {(fieldRequired) => (
              <Form.Item
                name="password"
                label={newPassword}
                colon={false}
                rules={[
                  {
                    required: true,
                    message: fieldRequired,
                  },
                  {
                    min: 8,
                    message: translations(messages.passwordLengthError),
                  },
                  {
                    max: 20,
                    message: translations(messages.passwordLengthError),
                  },
                ]}
                getValueFromEvent={(e) => e.target.value.trim()}
              >
                <Password size="large" />
              </Form.Item>
            )}
          </FormattedMessage>
        )}
      </FormattedMessage>
      <FormattedMessage {...messages.confirmPassword}>
        {(confirmPassword: string) => (
          <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: confirmPassword }}>
            {(fieldRequired) => (
              <Form.Item
                name="confirmPassword"
                label={confirmPassword}
                colon={false}
                dependencies={['password']}
                rules={[
                  {
                    required: true,
                    message: fieldRequired,
                  },
                  {
                    validator: validateConfirmPassword,
                  },
                ]}
                getValueFromEvent={(e) => e.target.value.trim()}
              >
                <Password size="large" />
              </Form.Item>
            )}
          </FormattedMessage>
        )}
      </FormattedMessage>
      <Button size="large" htmlType="submit">
        <FormattedMessage {...utilsMessages.change} />
      </Button>
    </Form>
  );
};

export default ChangePasswordForm;
